/**
 * Google Analytics Hook
 *
 * Automatically tracks page views on route changes in a React Router SPA.
 * Follows the same pattern as the ScrollToTop component in App.tsx.
 *
 * @example
 * // In App.tsx alongside ScrollToTop:
 * <AppProviders>
 *   <ScrollToTop />
 *   <GoogleAnalyticsTracker />
 *   <Outlet />
 * </AppProviders>
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initializeGA,
  trackPageView,
  trackEvent,
  setUserProperties,
  getAnalyticsStatus,
} from '@/services/tracking/googleAnalytics';
import type { EventParams } from '@/services/tracking/googleAnalytics';

/**
 * Hook that automatically tracks page views on route changes
 *
 * @returns Object with manual tracking methods for custom events
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const previousPath = useRef<string | null>(null);
  const pendingPageView = useRef<string | null>(null);

  // Initialize GA on first mount (async)
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      await initializeGA();
      if (mounted) {
        setIsInitialized(true);

        // Track any pending page view that occurred before initialization
        if (pendingPageView.current) {
          const title = document.title;
          trackPageView({
            path: pendingPageView.current,
            title,
          });
          pendingPageView.current = null;
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const currentPath = location.pathname;

    // Avoid tracking the same path twice (can happen with query param changes)
    if (previousPath.current === currentPath) {
      return;
    }

    previousPath.current = currentPath;

    // If not initialized yet, queue the page view
    if (!isInitialized) {
      pendingPageView.current = currentPath;
      return;
    }

    // Small delay to allow React Helmet to update the title
    const timeoutId = setTimeout(() => {
      const title = document.title;
      trackPageView({
        path: currentPath,
        title,
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, isInitialized]);

  // Return methods for manual event tracking
  return {
    trackEvent,
    setUserProperties,
    getStatus: getAnalyticsStatus,
    isInitialized,
  };
};

/**
 * React component that renders nothing but tracks page views
 * Use this as a sibling to ScrollToTop in your router
 *
 * @example
 * <AppProviders>
 *   <ScrollToTop />
 *   <GoogleAnalyticsTracker />
 *   <Outlet />
 * </AppProviders>
 */
export const GoogleAnalyticsTracker: React.FC = () => {
  useGoogleAnalytics();
  return null;
};

// Re-export types and utilities for convenience
export type { EventParams };
export { trackEvent, trackPageView, setUserProperties, getAnalyticsStatus };
